import React, { useEffect, useState } from 'react';
import WalletConnector from '../connectors/walletconnect';
import { MultisigAccount } from '../types/account';
import { TokenBalance } from '../types/token';
import { ModalType, NetworkType, SupportedNetworks } from '../utils/constants';
import { clearSessionKeys } from '../utils/networkConfigStore';
import { customNetworksEnabled } from '../utils/customNetworks';
import { clearKey } from '../signing/KeyManager';
import { useKeystoreAuth } from '../hooks/useKeystoreAuth';
import { useWalletConnect } from '../hooks/useWalletConnect';
import { useSession } from '../hooks/useSession';
import { useToast } from '../hooks/useToast';
import { PrivateKey } from '@hashgraph/sdk';

interface GlobalContextType {
  connection: {
    accountId: string;
    /** True once the wallet session is in place, restored after a reload or newly paired. */
    connected: boolean;
    connectWallet: () => void;
    connectToHashPack: () => void;
    connectViaWalletConnect: () => void;
    disconnectWallet: () => void;
    hashconnectConnectorInstance: WalletConnector;
    isHashPackExtensionAvailable: boolean;
  };
  modals: { [key: string]: { open: boolean } };
  sdk: any;
  balance: number;
  currentAccount: MultisigAccount;
  selectedToken: TokenBalance | null;
  updateAccount: (account: MultisigAccount) => void;
  updateBalance: (balance: number) => void;
  openModal: (modal: ModalType, data?: Record<string, any>) => void;
  closeModal: (modal: ModalType) => void;
  setSelectedToken: (token: TokenBalance | null) => void;
  connectedNetwork: NetworkType | undefined;
  updateConnectorNetwork: (network: NetworkType) => void;
  isPairing: boolean;
  refreshKey: number;
  triggerRefresh: () => void;
  handleKeystoreUnlock: (networkId: string, accountId?: string) => void;
  /** Signs in on a custom network with a decrypted local key; see hooks/useSession. */
  signInWithKey: (
    accountId: string,
    networkId: string,
    privateKey: Pick<PrivateKey, 'sign'>,
  ) => Promise<boolean>;
}

const initialModalsState = {
  [`${ModalType.FailedModal}`]: { open: false },
  [`${ModalType.SendTokensModal}`]: { open: false },
  [`${ModalType.CreateTokenModal}`]: { open: false },
  [`${ModalType.ConnectWalletModal}`]: { open: false },
  [`${ModalType.CreateTransactionModal}`]: { open: false },
  [`${ModalType.ContractInteractionModal}`]: { open: false },
  [`${ModalType.TokenDetailsModal}`]: { open: false },
  [`${ModalType.FundWalletModal}`]: { open: false },
  [`${ModalType.CreateTransactionWizard}`]: { open: false },
  [`${ModalType.FAQDrawer}`]: { open: false },
  [`${ModalType.AboutModal}`]: { open: false },
  [`${ModalType.NotificationDrawer}`]: { open: false },
  [`${ModalType.NotificationSettingsDrawer}`]: { open: false },
  [`${ModalType.AddressBookDrawer}`]: { open: false },
  [`${ModalType.PrivateNetworksDrawer}`]: { open: false },
  [`${ModalType.KeystoreAuthDrawer}`]: { open: false },
  [`${ModalType.ChangePasswordModal}`]: { open: false },
};

const contextInitialValue: GlobalContextType = {
  connection: {
    accountId: '',
    connected: false,
    connectWallet: () => {},
    connectToHashPack: () => {},
    connectViaWalletConnect: () => {},
    disconnectWallet: () => {},
    hashconnectConnectorInstance: {} as WalletConnector,
    isHashPackExtensionAvailable: false,
  },
  modals: initialModalsState,
  sdk: {},
  balance: 0,
  currentAccount: {} as MultisigAccount,
  selectedToken: null,
  updateAccount: (account: MultisigAccount) => {},
  updateBalance: (balance: number) => {},
  openModal: (modal: ModalType, data?: Record<string, any>) => {},
  closeModal: (modal: ModalType) => {},
  setSelectedToken: (token: TokenBalance | null) => {},
  connectedNetwork: undefined,
  updateConnectorNetwork: (network: NetworkType) => {},
  isPairing: false,
  refreshKey: 0,
  triggerRefresh: () => {},
  handleKeystoreUnlock: (networkId: string, accountId?: string) => {},
  signInWithKey: async () => false,
};

/** Stands in for the connector until WalletConnect has started, the same object on every render. */
const NO_CONNECTOR = {} as WalletConnector;

export const GlobalContext = React.createContext(contextInitialValue);

const getDefaultNetwork = (): NetworkType => {
  let currentNetwork: NetworkType | undefined =
    (localStorage.getItem('connectedNetwork') as NetworkType) || undefined;
  // A custom-network session saved earlier isn't restored while they're off.
  // Its account ID and token go too: carried onto a Hedera network, the account
  // ID would be logged in there as whoever owns that ID.
  if (currentNetwork?.startsWith('custom-') && !customNetworksEnabled()) {
    clearSessionKeys();
    currentNetwork = undefined;
  }
  if (!currentNetwork) {
    const network = (
      process.env.NODE_ENV === 'development' ? SupportedNetworks.Testnet : SupportedNetworks.Mainnet
    ).toLowerCase() as NetworkType;
    localStorage.setItem('connectedNetwork', network);

    return network;
  }

  return currentNetwork;
};

interface IGlobalProps {
  children: React.ReactNode;
}

export const GlobalProvider = ({ children }: IGlobalProps) => {
  const [sdk] = useState({});
  const [balance, setBalance] = useState(0);
  const [currentAccount, setCurrentAccount] = useState<MultisigAccount>({});
  const [modals, setModals] = useState(initialModalsState);
  const [selectedToken, setSelectedToken] = useState<TokenBalance | null>(null);
  const [connectedNetwork, setConnectedNetwork] = useState<NetworkType | undefined>(
    getDefaultNetwork(),
  );

  const {
    hashconnectConnectorInstance,
    connected,
    accountId,
    setAccountId,
    isPairing,
    isHashPackExtensionAvailable,
    connectWallet,
    connectViaWalletConnect,
    connectToHashPack,
    clearPairings,
  } = useWalletConnect(connectedNetwork);

  const updateAccount = (account: MultisigAccount) => {
    setCurrentAccount(account);
  };

  const updateConnectorNetwork = (network: NetworkType) => {
    if (network !== connectedNetwork) {
      disconnectWallet();
      setConnectedNetwork(network);
      localStorage.setItem('connectedNetwork', network);
    }
  };

  const updateBalance = (balance: number) => {
    setBalance(balance);
  };

  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const openModal = (modal: ModalType, data?: Record<string, any>) => {
    setModals(prev => ({
      ...prev,
      [`${modal}`]: {
        open: true,
        ...data,
      },
    }));
  };

  const closeModal = (modal: ModalType) => {
    setModals(prev => ({
      ...prev,
      [`${modal}`]: {
        open: false,
      },
    }));
  };

  const { handleKeystoreUnlock: rawKeystoreUnlock } = useKeystoreAuth(openModal);

  const handleKeystoreUnlock = (networkId: string, authAccountId?: string) => {
    rawKeystoreUnlock(networkId);
    if (authAccountId) {
      const switching = authAccountId !== accountId || networkId !== connectedNetwork;
      setAccountId(authAccountId);
      localStorage.setItem('accountId', authAccountId);
      setConnectedNetwork(networkId as NetworkType);
      localStorage.setItem('connectedNetwork', networkId);

      // Redirect to home when switching accounts on a custom network
      // to avoid the user staying on a dashboard that belongs to a different account.
      // Uses window.location (full reload) instead of React Router to ensure
      // complete state reset (activeKey, connection state, cached data).
      // Signing in again as the same account keeps the page.
      if (switching && window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
  };

  const { showToast } = useToast();
  const session = useSession({
    accountId,
    network: connectedNetwork,
    connected,
    connector: hashconnectConnectorInstance,
    signOut: () => signOut(),
    triggerRefresh,
    openModal,
    notify: (message, status) => showToast(message, status, undefined, 'top-right'),
  });

  const endConnection = () => {
    session.end();
    clearPairings();
    clearKey();

    // Clear only session keys, preserve network configs and keystores
    clearSessionKeys();
  };

  const disconnectWallet = () => {
    endConnection();

    // Reset connected network to default
    setConnectedNetwork(getDefaultNetwork());
  };

  /** Signs out but stays on this network: for a declined sign-in or a wallet connection that ended. */
  const signOut = () => {
    endConnection();
    if (connectedNetwork) localStorage.setItem('connectedNetwork', connectedNetwork);
  };

  const connection = {
    connected,
    accountId,
    connectWallet,
    connectToHashPack,
    connectViaWalletConnect,
    disconnectWallet,
    hashconnectConnectorInstance: hashconnectConnectorInstance || NO_CONNECTOR,
    isHashPackExtensionAvailable,
  };
  const contextValue = {
    sdk,
    connection,
    modals,
    openModal,
    closeModal,
    balance,
    updateBalance,
    currentAccount,
    selectedToken,
    updateAccount,
    setSelectedToken,
    connectedNetwork,
    updateConnectorNetwork,
    isPairing,
    refreshKey,
    triggerRefresh,
    handleKeystoreUnlock,
    signInWithKey: session.signInWithKey,
  };

  return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>;
};
